import { useMemo, useRef, useEffect, useState } from "react";
import axios from "axios";
import orgConfig from "../config/organization.json";
import { Section } from "../components/Section";
import { Row } from "../components/Row";
import { PrimaryButton, SubtleButton } from "../components/Buttons";

const API = "https://rohanbackend.axiomos.in/company";

export default function Organization() {
  const [org, setOrg] = useState({});
  const [busy, setBusy] = useState(false);

  const initialOrgRef = useRef({});

  // 🚀 Load existing organization on first render
  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    try {
      const res = await axios.get(API);
      setOrg(res.data || {});
      initialOrgRef.current = res.data || {};
    } catch (err) {
      console.error("❌ Fetch failed", err);
      setOrg({});
      initialOrgRef.current = {};
    }
  };

  // 👍 Detect unsaved changes
  const isDirty = useMemo(
    () => JSON.stringify(org) !== JSON.stringify(initialOrgRef.current),
    [org],
  );

  // ✍ Update fields
  const handleFieldChange = (key, value) => {
    setOrg((prev) => ({ ...prev, [key]: value }));
  };

  // 🔁 Auto slug generator
  const generateSlug = () => {
    const s = (org.name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    setOrg((p) => ({ ...p, slug: s }));
  };

  // 💾 Save or update the data
  const save = async () => {
    setBusy(true);
    try {
      await axios.put(API, org); // Update — always single entry
      alert("Organization saved successfully");
      await fetchOrganization(); // refresh UI
    } catch (err) {
      // If not found → create first time
      if (err?.response?.status === 404) {
        await axios.post(API, org);
        alert("Organization created successfully");
        await fetchOrganization();
      } else {
        alert("Save failed");
        console.error(err);
      }
    }
    setBusy(false);
  };

  // 🎯 Dynamic field renderer
  const renderFieldControl = (f) => {
    const value = org[f.key] ?? "";

    switch (f.type) {
      case "textarea":
        return (
          <textarea
            className="set-input"
            rows={3}
            placeholder={f.placeholder}
            value={value}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          />
        );

      case "select":
        return (
          <select
            className="set-input"
            value={value}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          >
            <option value="">Select</option>
            {(f.options || []).map((opt) => (
              <option value={opt} key={opt}>
                {opt}
              </option>
            ))}
          </select>
        );

      case "file":
        return (
          <input
            type="file"
            accept={f.accept}
            className="set-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              // Upload file to server
              const form = new FormData();
              form.append("image", file);
              form.append("folder", "organization");

              try {
                const res = await axios.post(`${API}/image`, form, {
                  headers: { "Content-Type": "multipart/form-data" },
                });

                const url = res.data.fileUrl;

                // Update field with URL returned
                handleFieldChange(f.key, url);
              } catch (err) {
                alert("Image upload failed");
                console.error(err);
              }
            }}
          />
        );

      case "slug":
        return (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="set-input"
              placeholder={f.placeholder}
              value={value}
              onChange={(e) =>
                handleFieldChange(
                  f.key,
                  e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                )
              }
            />
            <SubtleButton onClick={generateSlug}>Auto</SubtleButton>
          </div>
        );

      default:
        return (
          <input
            type={f.type || "text"}
            className="set-input"
            placeholder={f.placeholder}
            value={value}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          />
        );
    }
  };

  return (
    <Section
      title={orgConfig.title}
      desc={orgConfig.description}
      actions={
        <PrimaryButton disabled={busy || !isDirty} onClick={save}>
          {busy ? "Saving…" : orgConfig.saveLabel}
        </PrimaryButton>
      }
    >
      {orgConfig.groups.map((group, gi) => (
        <div key={gi} style={{ marginBottom: 24 }}>
          <h4 style={{ marginBottom: 10 }}>{group.groupTitle}</h4>
          <div className="form-grid">
            {group.fields.map((f) => (
              <Row
                key={f.key}
                label={f.required ? `${f.label} *` : f.label}
                hint={f.hint}
              >
                {renderFieldControl(f)}
              </Row>
            ))}
          </div>
        </div>
      ))}

      {/* 🌐 Preview */}
      <div className="org-preview-box">
        <h4>{orgConfig.preview.title}</h4>
        <p className="set-muted">{orgConfig.preview.caption}</p>

        {orgConfig.preview.showURL && (
          <div className="preview-url">
            https://
            <strong>
              {org.domain ? org.domain : `app.example.com/${org.slug}`}
            </strong>
          </div>
        )}

        {/* ---- Logo preview ---- */}
        {org.logo && (
          <div style={{ marginTop: 14 }}>
            <strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
              Logo Preview
            </strong>
            <img
              src={
                typeof org.logo === "string"
                  ? org.logo
                  : URL.createObjectURL(org.logo)
              }
              alt="logo"
              style={{
                maxHeight: 70,
                borderRadius: 6,
                background: "#fff",
                padding: 6,
              }}
            />
          </div>
        )}

        {/* ---- Favicon preview ---- */}
        {org.favicon && (
          <div style={{ marginTop: 14 }}>
            <strong style={{ fontSize: 13, display: "block", marginBottom: 6 }}>
              Favicon Preview
            </strong>
            <img
              src={
                typeof org.favicon === "string"
                  ? org.favicon
                  : URL.createObjectURL(org.favicon)
              }
              alt="favicon"
              style={{
                height: 40,
                width: 40,
                borderRadius: 4,
                background: "#fff",
                padding: 4,
              }}
            />
          </div>
        )}
      </div>
    </Section>
  );
}
